import React, { useState, useEffect } from 'react';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { Box, Modal, IconButton, Typography, CircularProgress, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';

interface PDFViewerProps {
    open: boolean;
    onClose: () => void;
    fileUrl: string;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ open, onClose, fileUrl }) => {
    const defaultLayoutPluginInstance = defaultLayoutPlugin();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Reset error state when fileUrl changes
    useEffect(() => {
        setLoading(true);
        setError(null);
        
        // Add an error handler for the PDF iframe
        const handlePdfError = () => {
            setLoading(false);
            setError('Failed to load the document. Please try again later.');
        };
        
        // Check if the URL returns a 400 error (attachment not processed)
        const checkUrl = async () => {
            try {
                const response = await fetch(fileUrl, { method: 'HEAD' });
                if (response.status === 400) {
                    setLoading(false);
                    setError('This attachment is still being processed by Cliniko. Please try again later.');
                }
            } catch (err) {
                // Ignore fetch errors, the PDF viewer will handle them
            }
        };
        
        checkUrl();
        
        // Add event listener for error events on window
        window.addEventListener('error', handlePdfError, { once: true });
        
        return () => {
            window.removeEventListener('error', handlePdfError);
        };
    }, [fileUrl]);

    const handleDocumentLoadSuccess = () => {
        setLoading(false);
        setError(null);
    };

    const handleRetry = () => {
        setLoading(true);
        setError(null);
        // Force reload by adding a timestamp to the URL
        const refreshedUrl = `${fileUrl}${fileUrl.includes('?') ? '&' : '?'}_t=${Date.now()}`;
        window.open(refreshedUrl, '_blank');
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="pdf-viewer-modal"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    width: '95vw',
                    height: '90vh',
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    boxShadow: 24,
                    overflow: 'hidden',
                }}
            >
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        zIndex: 1,
                        bgcolor: 'background.paper',
                        '&:hover': {
                            bgcolor: 'action.hover',
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <Box sx={{ height: '100%', width: '100%' }}>
                    {loading && (
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                flexDirection: 'column',
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                height: '100%' 
                            }}
                        >
                            <CircularProgress size={60} sx={{ mb: 2 }} />
                            <Typography variant="h6">Loading document...</Typography>
                        </Box>
                    )}
                    
                    {error && (
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                flexDirection: 'column',
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                height: '100%',
                                p: 3,
                                textAlign: 'center'
                            }}
                        >
                            <ErrorIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                Error Loading Document
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3, maxWidth: '600px' }}>
                                {error}
                            </Typography>
                            <Button 
                                variant="contained" 
                                startIcon={<RefreshIcon />}
                                onClick={handleRetry}
                            >
                                Retry
                            </Button>
                        </Box>
                    )}
                    
                    {!error && (
                        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                            <Viewer
                                fileUrl={fileUrl}
                                plugins={[defaultLayoutPluginInstance]}
                                defaultScale={1}
                                onDocumentLoad={handleDocumentLoadSuccess}
                            />
                        </Worker>
                    )}
                </Box>
            </Box>
        </Modal>
    );
};

export default PDFViewer; 